<script setup>
import { ref } from 'vue'

const props = defineProps({
  tabs: { type: Array, default: () => [] },
  activeTabId: { type: String, default: null },
  paneId: { type: String, required: true },
  // 是否允许拖入（悬浮窗模式不允许跨 pane 拖）
  droppable: { type: Boolean, default: true }
})

const emit = defineEmits(['switch', 'close', 'drop'])

const dragOverPaneId = ref(false)

// ---- 拖拽 ----

const handleDragStart = (e, tab) => {
  e.dataTransfer.effectAllowed = 'move'
  e.dataTransfer.setData('application/kb-tab', JSON.stringify({
    tabId: tab.tabId,
    sourcePaneId: props.paneId
  }))
}

const handleDragOver = (e) => {
  if (!props.droppable) return
  e.preventDefault()
  e.dataTransfer.dropEffect = 'move'
  dragOverPaneId.value = true
}

const handleDragLeave = () => {
  dragOverPaneId.value = false
}

const handleDrop = (e) => {
  if (!props.droppable) return
  e.preventDefault()
  dragOverPaneId.value = false

  try {
    const data = JSON.parse(e.dataTransfer.getData('application/kb-tab'))
    if (data.tabId && data.sourcePaneId) {
      emit('drop', {
        tabId: data.tabId,
        sourcePaneId: data.sourcePaneId,
        targetPaneId: props.paneId
      })
    }
  } catch {
    // ignore
  }
}
</script>

<template>
  <div
    class="pane-tab-bar"
    :class="{ 'drag-over': dragOverPaneId }"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <div
      v-for="tab in tabs"
      :key="tab.tabId"
      class="pane-tab"
      :class="{ active: tab.tabId === activeTabId }"
      draggable="true"
      @click="emit('switch', tab.tabId)"
      @dragstart="handleDragStart($event, tab)"
    >
      <span class="tab-icon">📄</span>
      <span class="tab-title">{{ tab.title }}</span>
      <button
        class="tab-close"
        title="关闭"
        @click.stop="emit('close', tab.tabId)"
      >✕</button>
    </div>

    <!-- 空占位（拖拽目标区域） -->
    <div v-if="tabs.length === 0" class="tab-placeholder">
      拖入页签或从目录打开文档
    </div>
  </div>
</template>

<style scoped>
.pane-tab-bar {
  display: flex;
  align-items: stretch;
  min-height: 36px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;
  gap: 1px;
  transition: background 0.15s;
}

.pane-tab-bar::-webkit-scrollbar {
  height: 2px;
}

.pane-tab-bar::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 1px;
}

.pane-tab-bar.drag-over {
  background: #dbeafe;
  box-shadow: inset 0 0 0 2px #60a5fa;
}

.pane-tab {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 10px;
  min-width: 0;
  max-width: 180px;
  height: 36px;
  background: #e5e7eb;
  cursor: pointer;
  user-select: none;
  font-size: 12px;
  color: #6b7280;
  transition: all 0.15s;
  border-radius: 6px 6px 0 0;
  flex-shrink: 0;
}

.pane-tab:hover {
  background: #dbeafe;
  color: #374151;
}

.pane-tab.active {
  background: white;
  color: #0284c7;
  font-weight: 600;
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.06);
}

.tab-icon {
  font-size: 13px;
  flex-shrink: 0;
}

.tab-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.tab-close {
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 3px;
  background: transparent;
  color: #9ca3af;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.12s;
  opacity: 0;
}

.pane-tab:hover .tab-close,
.pane-tab.active .tab-close {
  opacity: 1;
}

.tab-close:hover {
  background: #fecaca;
  color: #ef4444;
}

.tab-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  font-size: 12px;
  color: #9ca3af;
  padding: 0 12px;
  white-space: nowrap;
}
</style>
